import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ClipboardCheck,
  Wallet,
  ScanLine,
  MonitorCheck,
  LogOut,
  Languages,
  CalendarDays,
  FileSpreadsheet,
  ReceiptText,
  UserCheck,
  Menu,
  QrCode,
  BookOpen,
  GraduationCap,
  Camera,
} from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AvatarUpload from "@/components/app/PhotoUpload";
import { NotificationBell } from "@/components/app/NotificationBell";

const PMM_LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_89891435-6db1-403c-a368-9f416e4fb932/artifacts/ksi5basw_WhatsApp%20Image%202026-08-13%20at%207.58.19%20AM.jpeg";

const linksByRole = {
  admin: [
    { to: "/admin", key: "dashboard", icon: LayoutDashboard },
    { to: "/admin/students", key: "students", icon: GraduationCap },
    { to: "/admin/users", key: "users", icon: Users },
    { to: "/admin/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/admin/timetable", key: "timetable", icon: CalendarDays },
    { to: "/admin/enrollment", key: "enrollment", icon: UserCheck },
    { to: "/admin/fees", key: "fees", icon: ReceiptText },
    { to: "/admin/payments", key: "payments", icon: Wallet },
    { to: "/admin/progress", key: "progress", icon: Camera },
    { to: "/admin/reports", key: "reports", icon: FileSpreadsheet },
    { to: "/admin/qr", key: "wallQR", icon: QrCode },
    { to: "/admin/landing", key: "landingEditor", icon: BookOpen },
    { to: "/kiosk", key: "kiosk", icon: MonitorCheck },
  ],
  staff: [
    { to: "/staff", key: "dashboard", icon: LayoutDashboard },
    { to: "/staff/students", key: "students", icon: GraduationCap },
    { to: "/staff/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/staff/progress", key: "progress", icon: Camera },
    { to: "/kiosk", key: "kiosk", icon: MonitorCheck },
  ],
  teacher: [
    { to: "/teacher", key: "dashboard", icon: LayoutDashboard },
    { to: "/teacher/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/teacher/progress", key: "progress", icon: Camera },
    { to: "/teacher/reports", key: "classReport", icon: FileSpreadsheet },
  ],
  parent: [
    { to: "/parent", key: "dashboard", icon: LayoutDashboard },
    { to: "/parent/children", key: "myChildren", icon: GraduationCap },
    { to: "/parent/progress", key: "progress", icon: Camera },
    { to: "/parent/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/parent/fees", key: "fees", icon: ReceiptText },
    { to: "/parent/checkin", key: "scanQR", icon: ScanLine },
    { to: "/parent/timetable", key: "timetable", icon: CalendarDays },
  ],
};

export default function Layout({ children }) {
  const { user, logout, refresh } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return children;
  const links = linksByRole[user.role] || [];

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const uploadMyPhoto = async (dataUrl) => {
    try {
      await api.put("/users/me/photo", { photo_data_url: dataUrl });
      await refresh();
      toast.success(lang === "ms" ? "Gambar profil dikemas kini" : "Profile photo updated");
    } catch (err) {
      toast.error(lang === "ms" ? "Gagal memuat naik gambar" : "Failed to upload photo");
    }
  };

  return (
    <div className="min-h-screen grain-bg">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 shadow-lg shadow-blue-950/30 backdrop-blur"
        data-testid="topbar"
      >
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-1">
            {/* Mobile sidebar (hamburger) */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-xl text-white hover:bg-white/10 hover:text-white md:hidden"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 border-white/10 bg-gradient-to-b from-blue-950 to-[#0d2566] p-0 text-white">
                <SheetHeader className="border-b border-white/10 p-4 text-left">
                  <SheetTitle className="flex items-center gap-2 text-white">
                    <div className="rounded-xl bg-gradient-to-br from-yellow-300 via-yellow-500 to-orange-500 p-0.5">
                      <img src={PMM_LOGO} alt="Logo PMM" className="h-9 w-9 rounded-[0.65rem] object-cover" />
                    </div>
                    <span className="font-display text-lg font-extrabold uppercase tracking-tight">{t("appName")}</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 p-3" data-testid="mobile-sidebar-nav">
                  {links.map((l) => {
                    const Icon = l.icon;
                    const active =
                      loc.pathname === l.to ||
                      (l.to !== `/${user.role}` && loc.pathname.startsWith(l.to));
                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setMenuOpen(false)}
                        data-testid={`mnav-${l.key}`}
                        className={cn(
                          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-200",
                          active
                            ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-lg shadow-orange-500/30"
                            : "text-blue-100 hover:translate-x-1 hover:bg-white/10 hover:text-white",
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {t(l.key)}
                      </Link>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
            <Link to={`/${user.role}`} className="flex items-center gap-2.5">
              <div className="rounded-xl bg-gradient-to-br from-yellow-300 via-yellow-500 to-orange-500 p-0.5 shadow-lg shadow-yellow-500/20">
                <img src={PMM_LOGO} alt="Logo PMM" className="h-9 w-9 rounded-[0.65rem] object-cover" data-testid="app-header-logo" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-lg font-extrabold uppercase tracking-tight text-white">
                  {t("appName")}
                </div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400">
                  {t(`${user.role}_dashboard_title`)}
                </div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2 text-white">
            <NotificationBell />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLang(lang === "en" ? "ms" : "en")}
              data-testid="lang-toggle"
              className="gap-2 rounded-full border border-white/20 text-blue-100 hover:bg-white/10 hover:text-white"
            >
              <Languages className="h-4 w-4" />
              <span className="hidden sm:inline">{t("switchLanguage")}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={doLogout}
              data-testid="logout-btn"
              className="gap-2 rounded-full border-orange-400/60 bg-transparent text-orange-300 hover:bg-orange-500 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t("logout")}</span>
            </Button>
            <AvatarUpload
              name={user.name}
              photo={user.photo_data_url}
              onUpload={uploadMyPhoto}
              size="sm"
              testId="my-avatar"
            />
          </div>
        </div>
      </header>

      {/* Body layout — sidebar on md+, bottom nav on mobile */}
      <div className="relative overflow-x-clip">
        <div className="pointer-events-none absolute -left-32 top-10 h-80 w-80 rounded-full bg-orange-500/8 blur-3xl" />
        <div className="pointer-events-none absolute -right-32 top-64 h-80 w-80 rounded-full bg-blue-700/8 blur-3xl" />
        <div className="relative mx-auto flex max-w-7xl gap-6 px-4 pb-24 pt-6 sm:px-6 md:pb-8">
        <aside className="hidden w-60 shrink-0 md:block">
          <div className="sticky top-24 overflow-hidden rounded-[1.5rem] bg-gradient-to-b from-blue-950 to-[#12318a] shadow-xl shadow-blue-950/25">
            <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
            <nav className="flex flex-col gap-1 p-3">
              {links.map((l) => {
                const Icon = l.icon;
                const active =
                  loc.pathname === l.to ||
                  (l.to !== `/${user.role}` && loc.pathname.startsWith(l.to));
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.key}`}
                    className={cn(
                      "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-200",
                      active
                        ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-lg shadow-orange-500/30"
                        : "text-blue-100 hover:translate-x-1 hover:bg-white/10 hover:text-white",
                    )}
                  >
                    <span className={cn(
                      "grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors duration-200",
                      active ? "bg-white/20 text-white" : "bg-white/5 text-yellow-400 group-hover:bg-white/10",
                    )}>
                      <Icon className="h-4 w-4" />
                    </span>
                    {t(l.key)}
                  </Link>
                );
              })}
            </nav>
            <div className="mx-3 mb-3 mt-1 rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur" data-testid="sidebar-profile-card">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 font-display text-sm font-extrabold text-blue-950">
                  {user.photo_data_url ? (
                    <img src={user.photo_data_url} alt={user.name} className="h-full w-full object-cover" />
                  ) : (
                    (user.name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()
                  )}
                </div>
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-sm font-extrabold text-white">{user.name}</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-yellow-400">{t(user.role)}</div>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest text-blue-300/70">
              PMM — {lang === "ms" ? "Sejak" : "Since"} 2007
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1">{children}</main>
        </div>
      </div>

      {/* Bottom nav mobile */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 shadow-[0_-8px_24px_rgba(6,14,36,0.45)] backdrop-blur md:hidden"
        data-testid="bottom-nav"
      >
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
        <div className="mx-auto grid max-w-7xl grid-cols-5 gap-1 px-2 py-2">
          {links.slice(0, 5).map((l) => {
            const Icon = l.icon;
            const active =
              loc.pathname === l.to ||
              (l.to !== `/${user.role}` && loc.pathname.startsWith(l.to));
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`bnav-${l.key}`}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-[11px] font-semibold transition-all duration-200",
                  active
                    ? "bg-gradient-to-b from-orange-600 to-orange-500 text-white shadow-lg shadow-orange-500/30"
                    : "text-blue-200 hover:text-white",
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="leading-none">{t(l.key)}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

